import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn';

type ToastVariant = 'info' | 'success' | 'warning' | 'danger';

interface ToastItem {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
}

interface ToastContextValue {
  toast: (item: Omit<ToastItem, 'id'>) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TOAST_DURATION_MS = 5000;

const variantClasses: Record<'info', string> = {
  info: 'border-info-500/30 bg-white text-on-surface',
};

function StatusToast({
  item,
  onDismiss,
  icon,
  iconWrapClass,
  barClass,
  assertive,
}: {
  item: ToastItem;
  onDismiss: () => void;
  icon: string;
  iconWrapClass: string;
  barClass: string;
  assertive?: boolean;
}) {
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        bar.style.width = '0%';
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, []);

  return (
    <div
      className="pointer-events-auto relative w-full overflow-hidden rounded-xl bg-white p-4 text-dash-on-surface shadow-md"
      role={assertive ? 'alert' : 'status'}
    >
      <div className={cn('flex gap-4', item.description ? 'items-start' : 'items-center')}>
        <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-full', iconWrapClass)}>
          <span
            className="material-symbols-outlined text-[22px] leading-none"
            style={{ fontVariationSettings: '"FILL" 1' }}
            aria-hidden="true"
          >
            {icon}
          </span>
        </div>
        <div className={cn('min-w-0 flex-1', item.description && 'pr-6')}>
          <h3 className="text-base font-semibold leading-none text-dash-on-surface">{item.title}</h3>
          {item.description && (
            <p className="mt-1 text-[13px] leading-[18px] text-dash-on-surface-variant">{item.description}</p>
          )}
        </div>
        <button
          type="button"
          className={cn(
            'rounded-lg p-1 text-dash-on-surface-variant transition-colors hover:bg-dash-surface-container hover:text-dash-on-surface',
            item.description ? 'absolute top-4 right-4' : 'shrink-0',
          )}
          aria-label="Cerrar"
          onClick={onDismiss}
        >
          <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
            close
          </span>
        </button>
      </div>
      <div className="-mx-4 -mb-4 mt-4 h-1 overflow-hidden rounded-b-xl bg-dash-surface-container">
        <div
          ref={barRef}
          className={cn('h-full w-full rounded-r', barClass)}
          style={{ transition: `width ${TOAST_DURATION_MS}ms linear` }}
        />
      </div>
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const toast = useCallback((item: Omit<ToastItem, 'id'>) => {
    const id = crypto.randomUUID();
    setItems((prev) => [...prev, { ...item, id }]);
    setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), TOAST_DURATION_MS);
  }, []);

  const dismiss = (id: string) => setItems((prev) => prev.filter((t) => t.id !== id));

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="pointer-events-none fixed top-[calc(var(--moodle-navbar-height,80px)+0.75rem)] left-1/2 z-toast flex w-full max-w-[420px] -translate-x-1/2 flex-col items-center gap-3 px-4">
        {items.map((item) =>
          item.variant === 'success' || item.variant === 'danger' || item.variant === 'warning' ? (
            <StatusToast
              key={item.id}
              item={item}
              onDismiss={() => dismiss(item.id)}
              assertive={item.variant !== 'success'}
              icon={item.variant === 'danger' ? 'error' : item.variant === 'warning' ? 'warning' : 'check_circle'}
              iconWrapClass={
                item.variant === 'danger'
                  ? 'bg-danger/15 text-danger'
                  : item.variant === 'warning'
                    ? 'bg-warning-500/15 text-warning-500'
                    : 'bg-brand-100 text-dash-on-surface'
              }
              barClass={
                item.variant === 'danger' ? 'bg-danger' : item.variant === 'warning' ? 'bg-warning-500' : 'bg-dash-secondary'
              }
            />
          ) : (
            <div
              key={item.id}
              className={cn(
                'pointer-events-auto w-full rounded-lg border px-4 py-3 shadow-md',
                variantClasses[item.variant],
              )}
              role="status"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold">{item.title}</p>
                  {item.description && (
                    <p className="mt-0.5 text-xs text-on-surface-variant">{item.description}</p>
                  )}
                </div>
                <button
                  type="button"
                  className="text-on-surface-variant hover:text-on-surface"
                  aria-label="Cerrar"
                  onClick={() => dismiss(item.id)}
                >
                  ×
                </button>
              </div>
            </div>
          ),
        )}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
